import { useMemo, useState } from "react"
import axios from "axios"
import nProgress from "nprogress"
import readXlsxFile from "read-excel-file/browser"
import toast from "react-hot-toast"
import { Modal } from "components"
import { lang } from "config"

const stripControlChars = (value) =>
  String(value || "")
    .split("")
    .map((char) => {
      const code = char.charCodeAt(0)
      return code < 32 || (code >= 127 && code <= 159) ? " " : char
    })
    .join("")

const normalizeLegacyName = (value) =>
  stripControlChars(value)
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase()

const normalizeHeader = (value) =>
  stripControlChars(value)
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase()

const detectDelimiter = (line) => {
  const candidates = [";", ",", "\t", "|"]
  let best = ","
  let bestCount = 0

  candidates.forEach((delimiter) => {
    const count = (line.match(new RegExp(`\\${delimiter}`, "g")) || []).length
    if (count > bestCount) {
      best = delimiter
      bestCount = count
    }
  })

  return best
}

const parseCsvLine = (line, delimiter) => {
  const values = []
  let current = ""
  let quoted = false

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i]
    const next = line[i + 1]

    if (char === '"' && quoted && next === '"') {
      current += '"'
      i += 1
      continue
    }

    if (char === '"') {
      quoted = !quoted
      continue
    }

    if (char === delimiter && !quoted) {
      values.push(current)
      current = ""
      continue
    }

    current += char
  }

  values.push(current)
  return values.map((value) => stripControlChars(value).trim())
}

const parseCsv = (text) => {
  const lines = String(text || "")
    .replace(/^\uFEFF/, "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n")
    .filter((line) => line.trim() !== "")

  if (lines.length < 2) {
    return []
  }

  const delimiter = detectDelimiter(lines[0])
  const headers = parseCsvLine(lines[0], delimiter).map((header) => normalizeHeader(header))

  return lines.slice(1).map((line, index) => {
    const values = parseCsvLine(line, delimiter)
    const row = { rowNumber: index + 2 }

    headers.forEach((header, headerIndex) => {
      row[header] = values[headerIndex] || ""
    })

    return row
  })
}

const buildImportRows = (rows) => {
  const errors = []
  const duplicateMap = new Map()
  const importRows = []

  rows.forEach((row) => {
    const productUnitId = String(row["PRODUCT UNIT ID"] || row.ID || "").trim()
    const legacyName = String(row["LEGACY NAME"] || row.DETAIL || "").trim()
    const normalizedLegacyName = normalizeLegacyName(legacyName)

    if (!productUnitId) {
      errors.push(`Row ${row.rowNumber}: id/product_unit_id kosong`)
      return
    }

    if (!legacyName) {
      errors.push(`Row ${row.rowNumber}: legacy name dan Detail kosong`)
      return
    }

    if (duplicateMap.has(normalizedLegacyName)) {
      duplicateMap.set(normalizedLegacyName, {
        ...duplicateMap.get(normalizedLegacyName),
        duplicateRows: [
          ...duplicateMap.get(normalizedLegacyName).duplicateRows,
          row.rowNumber,
        ],
      })
      return
    }

    const item = {
      rowNumber: row.rowNumber,
      legacy_product_name: legacyName,
      normalized_legacy_product_name: normalizedLegacyName,
      product_unit_id: productUnitId,
      duplicateRows: [],
    }

    duplicateMap.set(normalizedLegacyName, item)
    importRows.push(item)
  })

  duplicateMap.forEach((item) => {
    if (item.duplicateRows.length) {
      errors.push(
        `Row ${item.duplicateRows.join(", ")}: duplicate legacy name "${item.legacy_product_name}", memakai row ${item.rowNumber}`
      )
    }
  })

  return { importRows, errors }
}

const readFileAsText = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result || "")
    reader.onerror = () => reject(reader.error || new Error("Gagal membaca file"))
    reader.readAsText(file)
  })

const isXlsxFile = (file) => {
  const name = String(file?.name || "").toLowerCase()
  return name.endsWith(".xlsx") || name.endsWith(".xls")
}

const parseXlsx = async (file) => {
  const sheetRows = await readXlsxFile(file)

  if (!sheetRows.length) {
    return []
  }

  const headers = sheetRows[0].map((header) => normalizeHeader(String(header ?? "")))

  return sheetRows.slice(1)
    .filter((values) => values.some((value) => String(value ?? "").trim() !== ""))
    .map((values, index) => {
      const row = { rowNumber: index + 2 }

      headers.forEach((header, headerIndex) => {
        const raw = values[headerIndex]
        row[header] = raw === null || raw === undefined ? "" : String(raw).trim()
      })

      return row
    })
}

const Content = ({ onSuccess, userName }) => {
  const [file, setFile] = useState(null)
  const [rows, setRows] = useState([])
  const [errors, setErrors] = useState([])
  const [submitting, setSubmitting] = useState(false)

  const { importRows, errors: validationErrors } = useMemo(
    () => buildImportRows(rows),
    [rows]
  )

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files?.[0] || null
    setFile(selectedFile)
    setRows([])
    setErrors([])

    if (!selectedFile) {
      return
    }

    const xlsx = isXlsxFile(selectedFile)

    try {
      const parsedRows = xlsx
        ? await parseXlsx(selectedFile)
        : parseCsv(await readFileAsText(selectedFile))

      if (!parsedRows.length) {
        setErrors([`File ${xlsx ? "Excel" : "CSV"} kosong atau tidak punya data`])
        return
      }

      setRows(parsedRows)
    } catch (error) {
      setErrors([error?.message || `Gagal membaca file ${xlsx ? "Excel" : "CSV"}`])
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setErrors([])

    if (!file) {
      toast.error("Silakan pilih file CSV terlebih dahulu")
      return
    }

    if (validationErrors.length) {
      setErrors(validationErrors)
      toast.error("Perbaiki data CSV terlebih dahulu")
      return
    }

    if (!importRows.length) {
      toast.error("Tidak ada data mapping yang bisa di-import")
      return
    }

    const base = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")
    if (!base) {
      toast.error("REACT_APP_INVENTORY_API_URL belum diset")
      return
    }

    nProgress.start()
    setSubmitting(true)

    try {
      const failed = []

      for (const row of importRows) {
        try {
          await axios.post(`${base}/legacy-product-unit-mappings`, {
            legacy_product_name: row.legacy_product_name,
            product_unit_id: row.product_unit_id,
            notes: "Imported from legacy product CSV",
            created_by: userName || null,
            updated_by: userName || null,
          })
        } catch (error) {
          const fieldErrors = Object.values(error?.response?.data?.errors || {}).flat().filter(Boolean).join(", ")
          const message =
            fieldErrors ||
            error?.response?.data?.message ||
            error?.message ||
            "Gagal import row"

          failed.push(`Row ${row.rowNumber}: ${row.legacy_product_name} (${row.product_unit_id}) - ${message}`)
        }
      }

      if (failed.length) {
        setErrors(failed)
        toast.error(`${failed.length} mapping gagal di-import`)
        return
      }

      toast.success(`${importRows.length} legacy product mapping berhasil di-import`)
      onSuccess?.()
    } finally {
      nProgress.done()
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="mt-6 space-y-6 text-xs">
      <div className="space-y-2">
        <p className="text-sm text-neutral-700">
          Upload CSV atau Excel legacy product mapping.
        </p>
        <ul className="pl-5 space-y-1 list-disc text-[11px] text-neutral-600">
          <li>
            Header yang dipakai:
            <code className="px-1 mx-1 rounded bg-neutral-100">product_unit_id</code>
            atau
            <code className="px-1 mx-1 rounded bg-neutral-100">id</code>
            sebagai product unit dan
            <code className="px-1 mx-1 rounded bg-neutral-100">legacy_name</code>
            sebagai nama lama.
          </li>
          <li>
            Jika
            <code className="px-1 mx-1 rounded bg-neutral-100">legacy_name</code>
            kosong, importer memakai
            <code className="px-1 mx-1 rounded bg-neutral-100">detail</code>.
            Header tidak case-sensitive dan boleh pakai spasi atau underscore
            (mis. <code className="px-1 mx-1 rounded bg-neutral-100">legacy name</code>).
          </li>
        </ul>
      </div>

      <div className="space-y-2">
        <label className="block text-xs font-medium text-neutral-700">
          File CSV / Excel
        </label>
        <input
          type="file"
          accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          onChange={handleFileChange}
          className="block w-full text-xs text-neutral-700 border border-neutral-200 rounded-xl cursor-pointer file:mr-3 file:px-3 file:py-2 file:text-xs file:font-medium file:border-0 file:rounded-xl file:bg-neutral-800 file:text-white hover:file:bg-neutral-900"
        />
        {file && (
          <p className="text-[11px] text-neutral-500">
            Selected file: <span className="font-medium">{file.name}</span>
          </p>
        )}
      </div>

      {!!rows.length && (
        <div className="overflow-hidden border border-neutral-200 rounded-xl">
          <div className="flex items-center justify-between p-3 bg-neutral-50 text-[11px] text-neutral-700">
            <div>
              Rows: <b>{rows.length}</b>
            </div>
            <div>
              Ready import: <b>{importRows.length}</b>
            </div>
          </div>
          <div className="max-h-52 overflow-auto">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-white">
                <tr>
                  <th className="px-3 py-2 text-left text-[11px] font-medium text-neutral-500 uppercase">
                    Product Unit ID
                  </th>
                  <th className="px-3 py-2 text-left text-[11px] font-medium text-neutral-500 uppercase">
                    Legacy Name
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {importRows.slice(0, 80).map((row) => (
                  <tr key={`${row.product_unit_id}-${row.normalized_legacy_product_name}`}>
                    <td className="px-3 py-2 text-[11px] text-neutral-800">
                      {row.product_unit_id}
                    </td>
                    <td className="px-3 py-2 text-[11px] text-neutral-800">
                      {row.legacy_product_name}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(validationErrors.length > 0 || errors.length > 0) && (
        <div className="p-3 text-[11px] border rounded-xl border-red-200 bg-red-50 text-red-700 max-h-40 overflow-auto">
          <div className="mb-1 font-semibold">{lang?.error || "Error"}:</div>
          <ul className="pl-4 space-y-1 list-disc">
            {[...validationErrors, ...errors].map((message, index) => (
              <li key={index}>{message}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex items-center space-x-2">
        <button
          type="submit"
          disabled={submitting}
          className="items-center px-6 py-3 text-white transition bg-neutral-800 rounded-xl active:hover:scale-90 disabled:opacity-60"
        >
          <span>{submitting ? "Meng-import..." : "Import Legacy Mapping"}</span>
        </button>
      </div>
    </form>
  )
}

const ImportLegacyMapping = ({ isOpen, onClose, onSuccess, userName }) => {
  const handleSuccess = () => {
    onClose?.()
    onSuccess?.()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Import Legacy Product Mapping"
      content={<Content onSuccess={handleSuccess} userName={userName} />}
    />
  )
}

export default ImportLegacyMapping
